/**
 * Advanced Asset Search and Filter Component
 */

import { useState, useCallback, useEffect } from "react";
import {
  AssetType,
  AssetStatus,
  VenueType,
  AssetFilter,
  ASSET_TYPE_LABELS,
  ASSET_STATUS_LABELS,
  VENUE_TYPE_LABELS,
} from "../../types/asset-types";
import { useVenues } from "../../hooks/use-venues";

export interface SavedSearch {
  id: string;
  name: string;
  filter: AssetFilter;
  createdAt: string;
}

export interface AssetSearchFiltersProps {
  filter: AssetFilter;
  onFilterChange: (filter: AssetFilter) => void;
  onClearFilters: () => void;
  className?: string;
}

export function AssetSearchFilters({
  filter,
  onFilterChange,
  onClearFilters,
  className = "",
}: AssetSearchFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState(filter.search || "");
  const [selectedTypes, setSelectedTypes] = useState<AssetType[]>(
    filter.type ? [filter.type] : []
  );
  const [selectedStatuses, setSelectedStatuses] = useState<AssetStatus[]>(
    filter.status ? [filter.status] : []
  );
  const [selectedVenueId, setSelectedVenueId] = useState(filter.venueId || "");
  const [selectedVenueTypes, setSelectedVenueTypes] = useState<VenueType[]>([]);
  const [dateFrom, setDateFrom] = useState(filter.createdFrom || "");
  const [dateTo, setDateTo] = useState(filter.createdTo || "");
  const [selectedTags, setSelectedTags] = useState<string[]>(filter.tags || []);
  const [conversationFilter, setConversationFilter] = useState(
    filter.conversationId || ""
  );

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");

  // Load venues for venue filter
  const { venues } = useVenues({ autoFetch: true });

  // Update local state when filter prop changes
  useEffect(() => {
    setSearchQuery(filter.search || "");
    setSelectedTypes(filter.type ? [filter.type] : []);
    setSelectedStatuses(filter.status ? [filter.status] : []);
    setSelectedVenueId(filter.venueId || "");
    setDateFrom(filter.createdFrom || "");
    setDateTo(filter.createdTo || "");
    setSelectedTags(filter.tags || []);
    setConversationFilter(filter.conversationId || "");
  }, [
    filter.search,
    filter.type,
    filter.status,
    filter.venueId,
    filter.createdFrom,
    filter.createdTo,
    filter.tags,
    filter.conversationId,
  ]);

  // Load saved searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("asset-saved-searches");
    if (saved) {
      try {
        setSavedSearches(JSON.parse(saved));
      } catch (error) {
        console.error("Error loading saved searches:", error);
      }
    }
  }, []);

  // Build filter object from current state
  const buildFilter = useCallback((): AssetFilter => {
    const newFilter: AssetFilter = {};

    if (searchQuery.trim()) {
      newFilter.search = searchQuery.trim();
    }

    if (selectedTypes.length === 1) {
      newFilter.type = selectedTypes[0];
    }

    if (selectedStatuses.length === 1) {
      newFilter.status = selectedStatuses[0];
    }

    if (selectedVenueId) {
      newFilter.venueId = selectedVenueId;
    }

    if (dateFrom) {
      newFilter.createdFrom = dateFrom;
    }

    if (dateTo) {
      newFilter.createdTo = dateTo;
    }

    if (selectedTags.length > 0) {
      newFilter.tags = selectedTags;
    }

    if (conversationFilter) {
      newFilter.conversationId = conversationFilter;
    }

    return newFilter;
  }, [
    searchQuery,
    selectedTypes,
    selectedStatuses,
    selectedVenueId,
    dateFrom,
    dateTo,
    selectedTags,
    conversationFilter,
  ]);

  // Apply filters
  const applyFilters = useCallback(() => {
    const newFilter = buildFilter();
    onFilterChange(newFilter);
  }, [buildFilter, onFilterChange]);

  // Auto-apply filters when search query changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      applyFilters();
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchQuery, applyFilters]);

  // Apply filters when other controls change
  useEffect(() => {
    applyFilters();
  }, [
    applyFilters,
    selectedTypes,
    selectedStatuses,
    selectedVenueId,
    dateFrom,
    dateTo,
    selectedTags,
    conversationFilter,
  ]);

  // Handle type selection
  const handleTypeToggle = (type: AssetType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  // Handle status selection
  const handleStatusToggle = (status: AssetStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  // Handle venue type selection
  const handleVenueTypeToggle = (venueType: VenueType) => {
    setSelectedVenueTypes((prev) =>
      prev.includes(venueType)
        ? prev.filter((vt) => vt !== venueType)
        : [...prev, venueType]
    );
  };

  // Handle tag input
  const handleTagAdd = (tag: string) => {
    if (tag.trim() && !selectedTags.includes(tag.trim())) {
      setSelectedTags((prev) => [...prev, tag.trim()]);
    }
  };

  const handleTagRemove = (tag: string) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tag));
  };

  // Clear all filters
  const handleClearAll = () => {
    setSearchQuery("");
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSelectedVenueId("");
    setSelectedVenueTypes([]);
    setDateFrom("");
    setDateTo("");
    setSelectedTags([]);
    setConversationFilter("");
    onClearFilters();
  };

  // Save current search
  const handleSaveSearch = () => {
    if (!saveSearchName.trim()) return;

    const newSearch: SavedSearch = {
      id: Date.now().toString(),
      name: saveSearchName.trim(),
      filter: buildFilter(),
      createdAt: new Date().toISOString(),
    };

    const updatedSearches = [...savedSearches, newSearch];
    setSavedSearches(updatedSearches);
    localStorage.setItem(
      "asset-saved-searches",
      JSON.stringify(updatedSearches)
    );

    setSaveSearchName("");
    setShowSaveDialog(false);
  };

  // Load saved search
  const handleLoadSearch = (savedSearch: SavedSearch) => {
    onFilterChange(savedSearch.filter);
  };

  // Delete saved search
  const handleDeleteSearch = (searchId: string) => {
    const updatedSearches = savedSearches.filter((s) => s.id !== searchId);
    setSavedSearches(updatedSearches);
    localStorage.setItem(
      "asset-saved-searches",
      JSON.stringify(updatedSearches)
    );
  };

  // Check if any filters are active
  const hasActiveFilters = Object.keys(buildFilter()).length > 0;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`}>
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search assets by name, description, location, or metadata..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors ${
              isExpanded ? "bg-gray-50" : ""
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                />
              </svg>
              <span className="text-sm">Filters</span>
              {hasActiveFilters && (
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {Object.keys(buildFilter()).length}
                </span>
              )}
            </div>
          </button>

          {hasActiveFilters && (
            <button
              onClick={handleClearAll}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Advanced Filters */}
      {isExpanded && (
        <div className="p-4 space-y-6">
          {/* Saved Searches */}
          {savedSearches.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Saved Searches
              </label>
              <div className="flex flex-wrap gap-2">
                {savedSearches.map((savedSearch) => (
                  <div
                    key={savedSearch.id}
                    className="flex items-center bg-gray-100 rounded-lg px-3 py-1"
                  >
                    <button
                      onClick={() => handleLoadSearch(savedSearch)}
                      className="text-sm text-gray-700 hover:text-gray-900"
                    >
                      {savedSearch.name}
                    </button>
                    <button
                      onClick={() => handleDeleteSearch(savedSearch.id)}
                      className="ml-2 text-gray-400 hover:text-red-600"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Asset Types */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Asset Types
              </label>
              <div className="space-y-2">
                {Object.values(AssetType).map((type) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(type)}
                      onChange={() => handleTypeToggle(type)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {ASSET_TYPE_LABELS[type]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Asset Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="space-y-2">
                {Object.values(AssetStatus).map((status) => (
                  <label key={status} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status)}
                      onChange={() => handleStatusToggle(status)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {ASSET_STATUS_LABELS[status]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Venue Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Venue
              </label>
              <select
                value={selectedVenueId}
                onChange={(e) => setSelectedVenueId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Venues</option>
                {venues.map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name} ({VENUE_TYPE_LABELS[venue.type]})
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date
              </label>
              <div className="space-y-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  placeholder="From"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="To"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Conversation Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conversation ID
              </label>
              <input
                type="text"
                value={conversationFilter}
                onChange={(e) => setConversationFilter(e.target.value)}
                placeholder="Enter conversation ID"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Add tag and press Enter"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleTagAdd(e.currentTarget.value);
                      e.currentTarget.value = "";
                    }
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {selectedTags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                      >
                        {tag}
                        <button
                          onClick={() => handleTagRemove(tag)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Save Search */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-2">
              {!showSaveDialog ? (
                <button
                  onClick={() => setShowSaveDialog(true)}
                  disabled={!hasActiveFilters}
                  className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Save current search
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={saveSearchName}
                    onChange={(e) => setSaveSearchName(e.target.value)}
                    placeholder="Search name"
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                  <button
                    onClick={handleSaveSearch}
                    disabled={!saveSearchName.trim()}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setShowSaveDialog(false);
                      setSaveSearchName("");
                    }}
                    className="text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="text-sm text-gray-500">
              {hasActiveFilters &&
                `${Object.keys(buildFilter()).length} filters active`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
